/**
 * Step 6: Adaptive FAQ. Fetch /api/ux-faq avec timeout 150ms, cache 60s.
 * Fallback ordre par défaut si flags OFF, hors rollout, timeout ou erreur.
 */

import { useState, useEffect, useRef } from "react";
import { getUxFlags } from "@/lib/uxFlags";
import { UX_SESSION_STORAGE_KEY } from "@/lib/uxTracking";

const FETCH_TIMEOUT_MS = 150;
const CACHE_TTL_MS = 60_000;

export const DEFAULT_FAQ_ORDER = ["faq_about", "faq_privacy", "faq_timeline", "faq_price"] as const;
export type FaqId = "faq_about" | "faq_privacy" | "faq_timeline" | "faq_price";
export type FaqVariant = "trust_first" | "price_first" | "fast_first" | "default";

export interface AdaptiveFaqResult {
  faqVariant: FaqVariant;
  order: FaqId[];
  isAdaptive: boolean;
}

interface FaqCacheEntry {
  data: AdaptiveFaqResult;
  expiresAt: number;
}

let memoryCache: { sessionId: string; entry: FaqCacheEntry } | null = null;

function getCached(sessionId: string): AdaptiveFaqResult | null {
  if (!memoryCache || memoryCache.sessionId !== sessionId || Date.now() >= memoryCache.entry.expiresAt) {
    return null;
  }
  return memoryCache.entry.data;
}

function setCached(sessionId: string, data: AdaptiveFaqResult): void {
  memoryCache = {
    sessionId,
    entry: { data, expiresAt: Date.now() + CACHE_TTL_MS },
  };
}

const FALLBACK: AdaptiveFaqResult = {
  faqVariant: "default",
  order: [...DEFAULT_FAQ_ORDER],
  isAdaptive: false,
};

export function useAdaptiveFaq(): AdaptiveFaqResult {
  const [result, setResult] = useState<AdaptiveFaqResult>(FALLBACK);
  const fetched = useRef(false);

  useEffect(() => {
    let cancelled = false;
    const sessionId =
      typeof sessionStorage !== "undefined" ? sessionStorage.getItem(UX_SESSION_STORAGE_KEY) : null;

    const applyFallback = () => {
      if (!cancelled) setResult(FALLBACK);
    };

    const run = async () => {
      const flags = await getUxFlags();
      if (!flags.adaptiveUxEnabled || !flags.uxFaqEnabled || !sessionId) {
        applyFallback();
        return;
      }

      const cached = getCached(sessionId);
      if (cached) {
        if (!cancelled) setResult(cached);
        return;
      }

      const ac = new AbortController();
      const timeoutId = setTimeout(() => ac.abort(), FETCH_TIMEOUT_MS);

      try {
        const r = await fetch(`/api/ux-faq?session_id=${encodeURIComponent(sessionId)}`, {
          credentials: "same-origin",
          signal: ac.signal,
        });
        clearTimeout(timeoutId);
        if (cancelled) return;
        if (r.status !== 200) {
          applyFallback();
          return;
        }
        const data = (await r.json()) as { faqVariant: FaqVariant; order: FaqId[] };
        const next: AdaptiveFaqResult = {
          faqVariant: data.faqVariant ?? "default",
          order: Array.isArray(data.order) && data.order.length > 0 ? data.order : [...DEFAULT_FAQ_ORDER],
          isAdaptive: true,
        };
        setCached(sessionId, next);
        setResult(next);
      } catch {
        if (!cancelled) applyFallback();
      }
    };

    if (!fetched.current) {
      fetched.current = true;
      void run();
    }

    return () => {
      cancelled = true;
    };
  }, []);

  return result;
}
